import { useMemo } from "react";
import { toast } from "sonner";
import { RegulifyService } from "../../api/RegulifyService";
import { REF_LIMIT } from "./analysisUtils";
import { isPlainObject } from "./analysisUtils";
import DeepAnalysisSidebar from "./DeepAnalysisSidebar";
import SectionCard from "./SectionCard";
import LoadingCard from "../LoadingCard";

const SECTION_ORDER = [
  "title",
  "objective",
  "study_design",
  "population",
  "intervention",
  "comparator",
  "outcome_measures",
  "results",
  "safety_outcomes",
  "limitations",
  "conclusion",
];

const DeepAnalysisViewer = ({
  analysisJson,
  selectedPaperKey,
  onSelectPaper,
  productName,
  onAskLiterature,
  onOpenComparator,
}) => {
  const analysisByFile = useMemo(() => {
    if (!analysisJson || !isPlainObject(analysisJson)) return null;
    const raw =
      analysisJson.deep_analysis ??
      analysisJson.deepAnalysis ??
      analysisJson.result ??
      analysisJson.data ??
      analysisJson;
    return isPlainObject(raw) ? raw : null;
  }, [analysisJson]);

  const selectedPaperData = useMemo(() => {
    if (!analysisByFile || !selectedPaperKey) return null;
    return analysisByFile[selectedPaperKey] ?? null;
  }, [analysisByFile, selectedPaperKey]);

  const orderedSections = useMemo(() => {
    if (!selectedPaperData || !isPlainObject(selectedPaperData)) return [];
    const keys = Object.keys(selectedPaperData);
    return [...SECTION_ORDER.filter((k) => keys.includes(k)), ...keys.filter((k) => !SECTION_ORDER.includes(k))];
  }, [selectedPaperData]);

  const handleDownloadDocx = async () => {
    if (!productName) {
      toast.error("No device selected.");
      return;
    }
    try {
      const blob = await RegulifyService.dla_download_deep_report_docx(productName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `deep-analysis-report-${productName}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded.");
    } catch (e) {
      toast.error("Download failed.");
    }
  };

  if (!analysisByFile || Object.keys(analysisByFile).length === 0) {
    return (
      <div className="py-8">
        <LoadingCard
          message="Generating deep analysis…"
          subMessage="We will show the report as soon as it is ready."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 gap-4">
      <aside className="w-[25%] min-w-[220px] flex-shrink-0">
        <DeepAnalysisSidebar
          analysisByFile={analysisByFile}
          selectedPaperKey={selectedPaperKey}
          onSelectPaper={onSelectPaper}
        />
      </aside>
      <div className="min-w-0 flex-1 overflow-y-auto">
        {!selectedPaperData ? (
          <div className="rounded-xl border border-base-300 bg-base-100 p-8 text-center text-base-content/70">
            Select a paper from the list to view its analysis.
          </div>
        ) : (
          <div className="space-y-6 pb-6">
            <div className="rounded-xl border border-base-300 bg-base-100 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h1
                    className="max-w-[50ch] truncate text-[14px] font-semibold text-primary"
                    title={selectedPaperKey}
                  >
                    {selectedPaperKey}
                  </h1>
                  <p className="mt-1 text-sm text-base-content/70">
                    Sections and extracted insights from the analysis.
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={onOpenComparator}
                  >
                    Litr Comparator
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={onAskLiterature}
                  >
                    Literature Chat
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleDownloadDocx}
                  >
                    <span className="text-lg">⬇ Deep Analysis Report</span>
                  </button>
                </div>
              </div>
            </div>
            {orderedSections.map((sectionKey) => (
              <SectionCard
                key={sectionKey}
                sectionKey={sectionKey}
                sectionValue={selectedPaperData[sectionKey]}
                refLimit={REF_LIMIT}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeepAnalysisViewer;
